
import React, { useState, useEffect } from 'react';
import { useAppStore } from '../../store/useAppStore';
import { saveComponentToLibrary } from '../../services/firestoreService';
import { getFormTemplate } from '../../config/formTemplates';
import FormTypeModal from './FormTypeModal';
import FormConfigPanel from './FormConfigPanel';
import UniversalFormPreview from './UniversalFormPreview';

const FormBuilder = ({ onClose, onSave }) => {
  // Get app name from store for Firestore save
  const { appName } = useAppStore();

  // Local state management
  const [showTypeModal, setShowTypeModal] = useState(true);
  const [formConfig, setFormConfig] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Component mount lifecycle
  useEffect(() => {
    console.log("🟢 [FormBuilder] mounted");
    try {
      const test = getFormTemplate('contact');
      console.log("🔎 [FormBuilder] template lookup test:", Boolean(test));
    } catch (e) {
      console.error("❌ [FormBuilder] template test failed:", e);
    }
    return () => {
      console.log("🔴 [FormBuilder] unmounted");
    };
  }, []);

  // State change tracking
  useEffect(() => {
    console.log("📝 [FormBuilder] state:", {
      showTypeModal,
      hasFormConfig: Boolean(formConfig),
      formType: formConfig?.formType || null,
      isSaving
    });
  }, [showTypeModal, formConfig, isSaving]);

  // Handle form type selection from modal
  const handleFormTypeSelect = (formType) => {
    console.log("➡️ [FormBuilder] handleFormTypeSelect:", formType);
    try {
      const template = getFormTemplate(formType);
      if (!template) {
        throw new Error("No template found for type: " + formType);
      }
      setFormConfig(prev => {
        console.log("   ↳ setting formConfig (wasNull? ", !prev, ")");
        return template;
      });
      setShowTypeModal(false);
      console.log("   ↳ type modal closed, builder UI will render");
    } catch (error) {
      console.error("❌ [FormBuilder] getFormTemplate error:", error);
      alert('Error loading form template: ' + error.message);
    }
  };

  // Handle form configuration updates from config panel
  const handleUpdateForm = (newConfig) => {
    console.log("✏️ [FormBuilder] onUpdateForm (title:", newConfig?.title, ", fields:", newConfig?.fields?.length, ")");
    setFormConfig(newConfig);
  };

  // Save form to Firestore
  const handleSave = async () => {
    if (!formConfig) {
      console.warn("⚠️ [FormBuilder] Save ignored: no formConfig");
      return;
    }

    console.log("➡️ [FormBuilder] handleSave started");
    setIsSaving(true);
    try {
      const componentData = {
        type: 'Form',
        formType: formConfig.formType,
        name: `${formConfig.title} (${formConfig.formType})`,
        title: formConfig.title,
        description: formConfig.description,
        submitLabel: formConfig.submitLabel,
        fields: formConfig.fields,
        actions: formConfig.actions || [],
        ui: { themeTokens: 'default', spacingHints: 'responsive' },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'user',
        updatedBy: 'user'
      };

      console.group("💾 [FormBuilder] Saving to Firestore");
      console.log("   appName:", appName);
      console.log("   payload:", componentData);
      console.groupEnd();

      const success = await saveComponentToLibrary(appName, componentData);

      if (success) {
        console.log("✅ [FormBuilder] save success");
        alert(`✅ ${formConfig.title} saved successfully!`);
        onSave?.(componentData);
        onClose();
      } else {
        console.error("❌ [FormBuilder] save returned false");
        alert('❌ Failed to save form. Check console for details.');
      }
    } catch (error) {
      console.error("❌ [FormBuilder] save failed:", error);
      alert('❌ Save failed. Check console for details.');
    } finally {
      setIsSaving(false);
      console.log("⬅️ [FormBuilder] handleSave finished");
    }
  };

  // Handle close with confirmation
  const handleClose = () => {
    console.log("➡️ [FormBuilder] handleClose");
    if (formConfig) {
      const ok = window.confirm('Are you sure you want to close? Unsaved changes will be lost.');
      if (!ok) {
        console.log("↩️ [FormBuilder] close canceled");
        return;
      }
    }
    onClose();
  };

  // Handle preview submission
  const handlePreviewSubmit = (result) => {
    console.log("📋 [FormBuilder] Preview form submitted:", result);
  };

  return (
    <>
      {/* Form type selection modal */}
      <FormTypeModal
        isOpen={showTypeModal}
        onClose={handleClose}
        onSelectFormType={handleFormTypeSelect}
      />

      {/* Main builder interface */}
      {!showTypeModal && formConfig ? (
        <div
          style={{
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0,
            backgroundColor: '#FFFFFF', 
            zIndex: 9999, 
            display: 'flex', 
            flexDirection: 'column'
          }}
        >
          {/* Header with title and action buttons */}
          <div
            style={{
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              padding: '16px 24px', 
              backgroundColor: '#FFFFFF',
              borderBottom: '1px solid #E5E7EB', 
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ fontSize: '24px' }}>📋</div>
              <div>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' }}>
                  Form Builder
                </h2>
                <p style={{ margin: 0, fontSize: '14px', color: '#6B7280' }}>
                  {formConfig.formType} Form • {formConfig.fields.length} field{formConfig.fields.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={handleClose}
                style={{
                  padding: '10px 20px', 
                  fontSize: '14px', 
                  fontWeight: '600',
                  color: '#6B7280', 
                  backgroundColor: '#F9FAFB',
                  border: '1px solid #E5E7EB', 
                  borderRadius: '8px', 
                  cursor: 'pointer'
                }}
                onMouseOver={(e) => { 
                  e.target.style.backgroundColor = '#F3F4F6'; 
                  e.target.style.borderColor = '#D1D5DB'; 
                }}
                onMouseOut={(e) => { 
                  e.target.style.backgroundColor = '#F9FAFB'; 
                  e.target.style.borderColor = '#E5E7EB'; 
                }}
              >
                Cancel
              </button>

              <button
                onClick={handleSave}
                disabled={isSaving}
                style={{
                  padding: '10px 20px', 
                  fontSize: '14px', 
                  fontWeight: '600',
                  color: '#FFFFFF', 
                  backgroundColor: isSaving ? '#9CA3AF' : '#3B82F6',
                  border: 'none', 
                  borderRadius: '8px', 
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  minWidth: '100px'
                }}
                onMouseOver={(e) => { 
                  if (!isSaving) e.target.style.backgroundColor = '#2563EB'; 
                }}
                onMouseOut={(e) => { 
                  if (!isSaving) e.target.style.backgroundColor = '#3B82F6'; 
                }}
              >
                {isSaving ? (
                  <>
                    <div style={{
                      width: '14px', 
                      height: '14px',
                      border: '2px solid #FFFFFF', 
                      borderTop: '2px solid transparent',
                      borderRadius: '50%', 
                      animation: 'spin 1s linear infinite'
                    }} />
                    Saving...
                  </>
                ) : (
                  <>💾 Save Form</>
                )}
              </button>
            </div>
          </div>

          {/* Two-panel layout: config on left, preview on right */}
          <div style={{ 
            flex: 1, 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            overflow: 'hidden' 
          }}>
            {/* Left panel: form configuration */}
            <FormConfigPanel 
              formConfig={formConfig} 
              onUpdateForm={handleUpdateForm}
              appName={appName}
            />
            
            {/* Right panel: live form preview */}
            <div style={{ 
              padding: '24px', 
              overflowY: 'auto', 
              backgroundColor: '#F9FAFB' 
            }}>
              <UniversalFormPreview 
                formConfig={formConfig}
                onSubmit={handlePreviewSubmit}
                runtime={false}
              />
            </div>
          </div>
        </div>
      ) : null}

      {/* Spinner animation keyframes */}
      <style>
        {`
          @keyframes spin { 
            0% { transform: rotate(0deg); } 
            100% { transform: rotate(360deg); } 
          }
        `}
      </style>
    </>
  );
};

export default FormBuilder;